import { useEffect, useRef } from "react";
import { useAlerts, AlertType } from "@/contexts/AlertsContext";

const HEALTH_ALERTS_DISMISSED_KEY = "health-alerts-dismissed-ids";

type Selectors<T> = {
  getId: (s: T) => string;
  getName: (s: T) => string;
  getHealth: (s: T) => number;
};

type PostProcessOptions = {
  threshold?: number;
  stageTitle?: string;
  capabilityTitle?: string;
  maxItems?: number;
};

export function usePostLoadHealthAlertsPanel<S, C>(
  stages: S[] | undefined,
  stageSelectors: Selectors<S>,
  capabilities: C[] | undefined,
  capabilitySelectors: Selectors<C>,
  isDataReady: boolean,
  options?: PostProcessOptions
) {
  const { addAlert } = useAlerts();
  const firedSignatureRef = useRef<string>("");

  const threshold = options?.threshold ?? 75;
  const stageTitle = options?.stageTitle ?? "Stage health alert";
  const capabilityTitle = options?.capabilityTitle ?? "Capability health alert";
  const maxItems = options?.maxItems ?? 10;

  useEffect(() => {
    if (!isDataReady) return;

    // Read suppression list from localStorage
    let suppressedIds = new Set<string>();
    try {
      const stored = localStorage.getItem(HEALTH_ALERTS_DISMISSED_KEY);
      if (stored) {
        suppressedIds = new Set(JSON.parse(stored));
      }
    } catch (e) {
      console.error("Failed to read suppressed alerts", e);
    }

    // Identify recovered items (health >= threshold) and unsuppress them
    const recoveredIds: string[] = [];
    
    (stages ?? []).forEach(s => {
      const id = stageSelectors.getId(s);
      const health = stageSelectors.getHealth(s);
      if (suppressedIds.has(id) && typeof health === 'number' && health >= threshold) {
        recoveredIds.push(id);
      }
    });

    (capabilities ?? []).forEach(c => {
      const id = capabilitySelectors.getId(c);
      const health = capabilitySelectors.getHealth(c);
      if (suppressedIds.has(id) && typeof health === 'number' && health >= threshold) {
        recoveredIds.push(id);
      }
    });

    if (recoveredIds.length > 0) {
      recoveredIds.forEach(id => suppressedIds.delete(id));
      localStorage.setItem(HEALTH_ALERTS_DISMISSED_KEY, JSON.stringify([...suppressedIds]));
    }

    const markSuppressed = (name: string) => {
      try {
        const stored = localStorage.getItem(HEALTH_ALERTS_DISMISSED_KEY);
        const currentSet = new Set<string>(stored ? JSON.parse(stored) : []);
        if (!currentSet.has(name)) {
          currentSet.add(name);
          localStorage.setItem(HEALTH_ALERTS_DISMISSED_KEY, JSON.stringify([...currentSet]));
        }
      } catch (e) {
        console.error("Failed to update suppressed alerts", e);
      }
    };

    // Collect stages below threshold that are NOT suppressed
    const stagesBelow = (stages ?? [])
      .map((s) => ({
        id: stageSelectors.getId(s),
        name: stageSelectors.getName(s),
        health: stageSelectors.getHealth(s),
      }))
      .filter(
        (x) =>
          typeof x.id === "string" &&
          typeof x.name === "string" &&
          typeof x.health === "number" &&
          !Number.isNaN(x.health) &&
          Math.round(x.health) < threshold &&
          !suppressedIds.has(x.name)
      )
      .sort((a, b) => a.health - b.health);

    // Collect capabilities below threshold that are NOT suppressed
    const capsBelow = (capabilities ?? [])
      .map((c) => ({
        id: capabilitySelectors.getId(c),
        name: capabilitySelectors.getName(c),
        health: capabilitySelectors.getHealth(c),
      }))
      .filter(
        (x) =>
          typeof x.id === "string" &&
          typeof x.name === "string" &&
          typeof x.health === "number" &&
          !Number.isNaN(x.health) &&
          Math.round(x.health) < threshold &&
          !suppressedIds.has(x.name)
      )
      .sort((a, b) => a.health - b.health);

    // Build queue items
    const queueItems: Array<{
      type: AlertType;
      id: string;
      name: string;
      health: number;
      title: string;
    }> = [
      ...stagesBelow.map((x) => ({
        type: "stage" as AlertType,
        id: x.id,
        name: x.name,
        health: x.health,
        title: stageTitle,
      })),
      ...capsBelow.map((x) => ({
        type: "capability" as AlertType,
        id: x.id,
        name: x.name,
        health: x.health,
        title: capabilityTitle,
      })),
    ].slice(0, maxItems);

    if (queueItems.length === 0) {
      firedSignatureRef.current = "";
      return;
    }

    // Create signature to prevent re-firing
    const signature = queueItems.map((x) => `${x.type}:${x.id}:${Math.round(x.health)}`).join("|");
    if (signature === firedSignatureRef.current) return;
    firedSignatureRef.current = signature;

    // Add all alerts to the panel
    queueItems.forEach((item) => {
      markSuppressed(item.name);
      addAlert({
        type: item.type,
        title: item.title,
        nodeName: item.name,
        health: item.health,
      });
    });
  }, [isDataReady, stages, capabilities, stageSelectors, capabilitySelectors, threshold, stageTitle, capabilityTitle, maxItems, addAlert]);
}
